import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { GetServerSideProps } from 'next'
import Head from 'next/head'

import { COMMENT_TYPES, PAGE_SIZE } from 'types/comments'
import { Comment as CommentType } from 'services/apiTypes/comment.types'
import { DeckCardInfoType } from 'types/search'

import { CommentService } from 'services/comment.service'

import PageWrapper from 'components/misc/PageWrapper'
import CardBanner from 'components/misc/CardBanner'
import InlineAd from 'components/advertisments/InlineAd'
import Thread, { updateCommentChain } from 'components/comments/Thread'
import Paginator from 'components/elements/Paginator'
import Link from 'components/elements/Link'
import Icon from 'components/elements/Icon'
import SortingDropdown from 'components/forumPage/SortingDropdown'
import SubmissionSortingDropdown from 'components/comments/ContestSubmission/SubmissionSortingDropdown'
import ContestDeckListItem from 'components/comments/ContestSubmission/ContestDeckListItem'

import { deltaToString } from 'utils/Deltas'
import { LOG_ERROR_AND_REDIRECT } from 'utils/ErrorString'

import styles from './[threadId].module.scss'


export type Props = { thread: CommentType; threadId: number | string; page: number | string, contestSubmissions?: DeckCardInfoType[], noChildren?: boolean, contestPage?: boolean }

const ThreadPage = ({ thread, threadId, page, contestSubmissions, noChildren = false, contestPage = false }: Props) => {
  const router = useRouter()
  const orderBy = (router.query.orderBy || (!!contestSubmissions ? '-createdAt' : '-points')) as string

  const [rootPost, setRootPost] = useState(thread) // Duplicating prop state to allow for updating this state without needing to refresh the page

  const handleUpdateComment = (updatedComment: CommentType) => {
    if (!rootPost) return

    const updatedRootThread = updateCommentChain(rootPost, updatedComment)

    setRootPost(updatedRootThread)
  }

  useEffect(() => {
    setRootPost(thread)
  }, [thread])

  const news = rootPost?.type === COMMENT_TYPES.News || rootPost?.type === COMMENT_TYPES.Contest
  const postBody = deltaToString(thread.text)
  const totalPages = Math.ceil(rootPost.childrenCount / PAGE_SIZE.COMMENTS)

  return (
    <PageWrapper title={thread.title} description={postBody}>
      <Head>
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={thread.createdAt} />
        <meta property="article:author" content={thread.owner.username} />
        {thread.featured && <meta property="og:image" content={thread.featured} />}
        {/* {thread.featured && <meta property="og:image:width" content="764" />} */}
        {/* {thread.featured && <meta property="og:image:height" content="430" />} */}
        {/* {thread.featured && <meta property="og:image:type" content="image/jpeg" />} */}
        <meta name="author" content={thread.owner.username} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:label1" content="Created by" />
        <meta name="twitter:data1" content={thread.owner.username} />
        <meta name="twitter:label2" content="Comments" />
        <meta name="twitter:data2" content={`${thread.childrenCount}`} />
        <meta name="twitter:label3" content="Points" />
        <meta name="twitter:data3" content={`${thread.points}`} />
      </Head>
      <CardBanner
        src={rootPost?.featured}
        height="250px"
        artist
        title={
          <p>
            <Icon name={news ? 'newspaper outline' : 'comments outline'} />
            {rootPost.title}
            {rootPost.archived && ' (Archived)'}
            {rootPost.locked && ' (Locked)'}
          </p>
        }
      />
      <div className={styles.page}>
        <InlineAd id="thread-top" banner longBanner top removeOnNoAd />

        <div className={styles.controls}>
          {!contestSubmissions && <SortingDropdown defaultOrderBy="points" defaultDescending useCreatedAt />}
          {!!contestSubmissions && <SubmissionSortingDropdown defaultOrderBy="createdAt" defaultDescending />}
        </div>

        <div className={styles.content}>
          {/* Displays when user is viewing a comment thread independant of the root post */}
          {rootPost.originalPost && (
            <div className={styles.parentThreadInfo}>
              <p>
                You are viewing a single child comment thread. If you'd like to view the original post{' '}
                <Link href={`/forum/thread/${rootPost.originalPost}`}>Click here</Link>
              </p>
            </div>
          )}
          <Thread
            noCollapse
            rootPageComment
            archived={rootPost.archived}
            locked={rootPost.locked}
            comment={rootPost}
            onCommentUpdate={handleUpdateComment}
            newsPostStyling={news}
            contestPage={contestPage}
            noChildren={noChildren}></Thread>

            {contestSubmissions && <div className={styles.contestDecks}>
               {contestSubmissions.map(submission => (
                <ContestDeckListItem key={submission.id} submission={submission} />
              ))}
            </div>}
        </div>

        <Paginator
          activePage={Number(page) || 1}
          totalPages={totalPages}
          urlBeforePageNumber={`/forum/thread/${threadId}?orderBy=${orderBy}&page=`}
        />

        <InlineAd id="thread-bottom" banner longBanner billboard bottom />
      </div>
    </PageWrapper>
  )
}

export default ThreadPage

export const getServerSideProps: GetServerSideProps<Props> = async context => {
  try {
    const threadId = (context.params?.threadId || 0) as string

    const page = (context.query.page || 1) as string
    const orderBy = (context.query.orderBy || '-points') as string

    if (!threadId || !page) return { notFound: true }

    const thread = await new CommentService(context).details(threadId, page, orderBy)

    if (!thread.parent && thread.deck) return { props: { thread: generateRootCommentForDeck(thread), threadId, page } }

    return { props: { thread, threadId, page } }
  } catch (err) {
    
    return LOG_ERROR_AND_REDIRECT(err, context)
  }
}

const generateRootCommentForDeck = (thread: CommentType): CommentType => {
  const deckName = thread.deck?.name || 'Deck'
  const deckId = thread.deck?.id || 0

  const fauxRootComment = `{"ops":[{"insert":"${deckName} comments\\n\\n"},{"attributes":{"link":"/decks/${deckId}"},"insert":"Deck link!"},{"insert":"\\n"}]}`

  return {
    ...thread,
    text: fauxRootComment,
  }
}
